import { createJSONStorage } from "zustand/middleware";
import LocalStorageKey from "@enums/LocalStorageKey";
import LocalStorageService from "@services/LocalStorageService";
import WorkspaceContextService from "@services/WorkspaceContextService";

/**
 * Resolves the persist key at read/write time so company name is available
 * after profile loads. Prefers the tab-scoped workspace (sessionStorage)
 * over the shared localStorage WORKSPACE key so two tabs on different
 * workspaces write to distinct persist slots and never clobber each other.
 */
export function getAppStorePersistKey(name: string): string {
  const tabWorkspace = WorkspaceContextService.companyName();
  const workspace = tabWorkspace
    ?? LocalStorageService.getItem<string>(LocalStorageKey.WORKSPACE)
    ?? '';
  return `${LocalStorageKey.APP_STORAGE_PREFIX}_${workspace}_${name}`;
}

/** zustand `persist` storage bound to a workspace-scoped localStorage key. */
export const workspaceScopedStorage = <T,>(name: string) =>
  createJSONStorage<T>(() => ({
    getItem: () => {
      try {
        return localStorage.getItem(getAppStorePersistKey(name));
      } catch {
        return null;
      }
    },
    setItem: (_name: string, value: string) => {
      try {
        localStorage.setItem(getAppStorePersistKey(name), value);
      } catch {
        // no-op
      }
    },
    removeItem: () => {
      try {
        localStorage.removeItem(getAppStorePersistKey(name));
      } catch {
        // no-op
      }
    },
  }));
